import { Link } from "react-router-dom";
import Header from "../components/Header";

const SECTIONS = [
  {
    h: "1. Who we are",
    p: [
      `PriceGuard NZ ("we", "us", "our") is operated by Aubrae Limited (NZBN issued; trading as PriceGuard NZ), based in Christchurch, New Zealand. This Privacy Policy explains how we collect, use, store, and disclose personal information you give us when you use https://priceguard.nz (the "Service").`,
      `We comply with the New Zealand Privacy Act 2020 and its thirteen Information Privacy Principles (IPPs). If anything below is unclear, email support@priceguard.nz and a real Kiwi will get back to you.`,
    ],
  },
  {
    h: "2. What we collect",
    p: [
      `Account data - email address, name (optional), hashed password (we never store plain-text passwords), and an authentication method (email/password or Google sign-in).`,
      `Product data you give us - the URL of a product you want monitored, the price you paid, the purchase date, and any optional notes or images you upload to your watchlist.`,
      `Receipt data - images and PDFs of receipts you choose to upload to your vault. We read each one with AI to extract the retailer, total, items, and warranty information so you can search and reference them later. Receipt files are stored encrypted at rest and are visible only to you.`,
      `Usage data - basic technical logs (IP address, browser, timestamps) used to keep the service running, prevent abuse, and debug errors.`,
      `Payment data - handled entirely by Stripe. We never see or store your full card number, CVC, or bank credentials. We store only a Stripe session reference, the amount paid (NZD), and the plan you bought, so we can confirm your Pro access.`,
    ],
  },
  {
    h: "3. Why we collect it",
    p: [
      `To run the core service - checking the price of your tracked purchases each day, comparing it to the retailer's protection window, and alerting you when a refund opportunity appears.`,
      `To communicate with you - transactional emails (price drops, expiry warnings, password resets, payment receipts). We do not sell your data to advertisers and we do not run third-party advertising on the site.`,
      `To keep the platform secure - rate-limiting, abuse detection, fraud prevention on payments.`,
      `To improve the product - aggregated, de-identified statistics (e.g. "members tracked X items this month") may be shown publicly. Individual emails and receipts are never shown publicly.`,
    ],
  },
  {
    h: "4. Who we share data with",
    p: [
      `Stripe Payments Europe Ltd - to process card payments. Stripe is PCI-DSS certified and operates under its own Privacy Policy.`,
      `Resend - to deliver transactional emails (price drop alerts, password resets, receipts). Resend processes only the recipient email address and the email body.`,
      `Google - only if you sign in with Google. We receive only your email, name, and profile picture, per the OAuth consent screen.`,
      `OpenAI / Gemini (via the Emergent Universal LLM Key) - when drafting a price-protection request or analysing a receipt photo, the relevant product details (name, price, retailer, receipt text) are sent to the AI model. We do not send your email address or payment data to AI providers.`,
      `Government or law enforcement - only if compelled by a valid New Zealand legal order.`,
      `We do not sell or rent your personal information to third parties for marketing.`,
    ],
  },
  {
    h: "5. Where your data is stored",
    p: [
      `Your account data and product-tracking history live in a managed MongoDB database. We use industry-standard at-rest encryption and TLS in transit. Data may be processed in the United States or the European Union by Stripe, Resend, and our AI providers, but we choose vendors with adequate-privacy frameworks aligned with the NZ Privacy Act.`,
    ],
  },
  {
    h: "6. How long we keep it",
    p: [
      `Account data - until you delete your account or ask us to remove it.`,
      `Tracked purchases and price history - until you delete the item or your account.`,
      `Payment records - kept for 7 years for tax purposes, as required under New Zealand law.`,
      `Email logs - automatically purged after 90 days.`,
    ],
  },
  {
    h: "7. Your rights",
    p: [
      `You can access, correct, or delete any personal information we hold about you at any time. Most of it is editable directly inside your dashboard. For deletion of your account and all associated data, email support@priceguard.nz with the subject "Delete my account" and we'll action it within 5 working days.`,
      `You can also ask for a portable export of your data in JSON format - same email, subject "Data export".`,
    ],
  },
  {
    h: "8. Cookies and analytics",
    p: [
      `PriceGuard NZ uses only essential cookies - specifically, a secure session cookie that keeps you signed in. We do not use third-party advertising trackers, social-media pixels, or behavioural analytics.`,
    ],
  },
  {
    h: "9. Children",
    p: [
      `The Service is intended for users aged 16 or older. We do not knowingly collect data from anyone under 16. If you believe a child has registered, email support@priceguard.nz and we'll remove the account.`,
    ],
  },
  {
    h: "10. Security",
    p: [
      `Passwords are hashed using bcrypt. All traffic is encrypted with TLS. Sensitive operations (login, password reset, payment) are rate-limited. We are a small team and won't pretend to be unbreakable - but we take security seriously and will notify affected users and the Office of the Privacy Commissioner within 72 hours of any confirmed notifiable privacy breach involving their personal data, in line with section 114 of the Privacy Act 2020.`,
    ],
  },
  {
    h: "10A. No automated decisions about you",
    p: [
      `PriceGuard NZ does not run automated decision-making that produces legal or similarly significant effects about you. The AI we use drafts emails and reads receipts; it does not score you, rank you, profile you, or decide whether you receive a refund. The refund decision is always made by the retailer, by a human, on the retailer's side.`,
    ],
  },
  {
    h: "10B. Your rights under the Privacy Act 2020",
    p: [
      `You have the right to: (a) ask what personal information we hold about you; (b) ask for it to be corrected if it is wrong; (c) ask for it to be deleted (subject to legal record-keeping obligations like tax law); (d) complain to us directly; and (e) complain to the Office of the Privacy Commissioner (privacy.org.nz) if you believe we have mishandled your data. We handle access and correction requests at no charge within 20 working days, as required by Information Privacy Principles 6 and 7.`,
    ],
  },
  {
    h: "11. Changes to this policy",
    p: [
      `We may update this Privacy Policy from time to time. Material changes will be announced via email to your registered address and via a banner on the site. Continued use of the Service after a change constitutes acceptance.`,
    ],
  },
  {
    h: "12. How to contact us",
    p: [
      `Aubrae Limited - trading as PriceGuard NZ`,
      `Christchurch, New Zealand`,
      `Privacy queries: support@priceguard.nz`,
    ],
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <Header />
      <main className="max-w-[900px] mx-auto px-6 lg:px-10 py-16">
        <div className="pg-overline mb-3">Legal</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Privacy Policy<span className="text-[#002FA7]">.</span></h1>
        <p className="text-[#52525b] mt-4 text-sm font-mono-pg">Last updated: February 2026</p>

        <div className="mt-12 space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.h} data-testid={`privacy-section-${s.h.split('.')[0].trim()}`}>
              <h2 className="font-heading text-xl font-bold tracking-tight">{s.h}</h2>
              <div className="mt-3 space-y-3">
                {s.p.map((line, i) => (
                  <p key={i} className="text-[#0a0a0a] leading-relaxed text-base">{line}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 border-t border-[#e4e4e7] pt-8 text-sm font-mono-pg text-[#52525b]">
          <Link to="/terms" className="hover:text-black underline" data-testid="privacy-terms-link">Read the Terms of Service →</Link>
        </div>
      </main>
    </div>
  );
}
